<template>
  <payment-container v-model="_selected" v-bind="containerProps">
    <img src="~/assets/images/integrations/stripe.png" class="max-h-8 max-w-14 opacity-85">

    <template #control>
      <div v-if="active" class="mt-4">
        <o-alert v-if="isStatusAuthError" show :title="$t('integration.needsReconnecting')" class="text-sm" variant="warning">
          <p>
            {{ $t('integration.stripe.reconnectInfo') }}
          </p>
        </o-alert>

        <o-alert v-if="isStatusSetupIncomplete" show :title="$t('integration.setupIncomplete')" class="text-sm" variant="warning">
          <p>
            {{ $t('integration.stripe.setupIncompleteInfo') }}
          </p>
        </o-alert>

        <template v-if="isStatusConnected">
          <div v-if="!loaded && !error" class="w-full text-center my-8">
            <o-loader :label="$t('integration.stripe.loading')" size="20px" class="mx-auto mb-3" />
            <p class="text-sm text-gray-700 dark:text-gray-200">
              {{ $t('integration.stripe.loading') }}
            </p>
          </div>

          <div v-if="error" class="w-full my-8 text-sm">
            <o-alert variant="danger" :title="error" show />
          </div>

          <div v-if="loaded" class="w-full my-6 text-sm border rounded py-3 px-3 bg-white">
            <StripeElements
              v-slot="{ elements }"
              ref="elms"
              :stripe-key="stripeKey"
              :instance-options="instanceOptions"
              :elements-options="elementsOptions"
            >
              <StripeElement
                ref="cardRef"
                type="card"
                :elements="elements"
                :options="cardOptions"
              />
            </StripeElements>
          </div>

          <div v-if="loaded && !error" class="text-sm -mt-4">
            <checkbox-field v-model="rememberCard" :label="$t('components.order.elements.payments.integration.stripe.manual.saveCardDetails')" />
          </div>
        </template>
      </div>
    </template>
  </payment-container>
</template>

<script setup lang="ts">
import { v4 as uuid } from 'uuid'

import { StripeElements, StripeElement } from 'vue-stripe-js'

import {
  OAlert,
} from '@/components/elements'

import {
  CheckboxField,
} from '@/components/fields'

import PaymentContainer from '@/components/order/elements/payments/PaymentContainer.vue'
import { Integration } from '@/_api/models/integration'

const { $api, $i18n } = useNuxtApp()

interface Props {
  order: any
  amount: string | number
  tip?: number | string | null
  option: any
  selected?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  tip: null,
  selected: null,
})

const emit = defineEmits<{
  (e: 'update:selected', value: string): void
}>()

const _selected = computed({
  get: () => props.selected,
  set: (value: string) => emit('update:selected', value),
})

const active = computed(() => props.option.guid === props.selected)
const integration = computed(() => new Integration(props.option.data))

const elms = ref<any>()
const cardRef = ref<any>()

const instanceOptions = ref({})
const elementsOptions = ref({})
const cardOptions = ref({ value: { postalCode: '' } })
const rememberCard = ref(false)
const loaded = ref(false)
const error = ref<string | false>(false)

const containerProps = computed(() => ({
  label: $i18n.t('integration.chargeCard'),
  info: $i18n.t('integration.stripe.label'),
  option: props.option,
}))

const stripeKey = computed(() => props.option.data.data.authData.stripePublishableKey)
const isStatusDisconnected = computed(() => integration.value.isStatusDisconnected)
const isStatusConnected = computed(() => integration.value.isStatusConnected)
const isStatusSetupIncomplete = computed(() => integration.value.isStatusSetupIncomplete)
const isStatusAuthError = computed(() => integration.value.isStatusAuthError)

onMounted(() => {
  if ((window as any).Stripe) {
    nextTick(() => {
      setup()
    })
  } else {
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = 'https://js.stripe.com/v3/'
    document.body.appendChild(script)

    script.onload = () => {
      nextTick(() => {
        setup()
      })
    }
  }
})

async function process() {
  const stripeToken = await tokenize()
  const idempotency_key = uuid()

  const params = {
    ...integration.value.data?.params,
    stripeToken,
    rememberCard: rememberCard.value,
  }

  const orderId = props.order.idOrder?.toString() || props.order.objectID?.()
  const response = await $api.integration.processPayment(integration.value.guid, {
    orderId,
    amount: Number(props.amount),
    params,
    idempotencyKey: idempotency_key,
  })

  if (!response) {
    throw new Error($i18n.t('integration.paymentFailed'))
  }

  return await saturate()
}

function setup() {
  loaded.value = true
}

async function tokenize() {
  const groupComponent = elms.value
  const cardComponent = cardRef.value

  const cardElement = cardComponent.stripeElement

  const result = await groupComponent.instance.createToken(cardElement)

  if (result.token && result.token.id) {
    return result.token.id
  } else {
    error.value = result.error?.message
    throw new Error(result.error)
  }
}

async function saturate() {
  const response = await $api.order.get(String(props.order.idOrder))
  return response.data
}

defineExpose({ process })
</script>
